/**
 * In-stage game state-shape contracts — slice 3 (first playable).
 *
 * The **server-authoritative**, Colyseus-Schema-replicated state for a running
 * stage. Slice 2 covered lobby state (`lobby.ts#RoomState`); this adds the
 * *dynamic* per-player physics state the GameRoom simulates and broadcasts
 * once the room enters the in-stage phase.
 *
 * Same pattern as `lobby.ts`: these are the **plain-TS shapes** the Developer's
 * Colyseus `Schema` subclasses mirror field-for-field (in `server/**`), and the
 * **read model** the client decodes the synced state into — so neither side
 * imports the other's `Schema` classes.
 *
 * Authority invariant (MASTER_PROMPT §4 rule 3): the **server** owns every
 * field here. The server runs a fixed-tick physics sim (see
 * `level.ts#PHYSICS` + `docs/design/contracts/slice3-realtime-protocol.md`),
 * writes the result into this Schema, and Colyseus replicates the delta. The
 * client **only renders** this state (slice 3 = pure server-render +
 * interpolation; no client-side prediction — kept thin, server is sole truth).
 * The client never writes position; it sends input *intent* via
 * `ClientMessage.Input` and the server decides the outcome (especially clear).
 *
 * Coordinate space matches `level.ts`: tiles, y up (ground top at y=0).
 */
/**
 * The all-players-at-the-door clear predicate (slice-3 co-op gate, acceptance
 * criteria 9 / 12 / 13). The **server** is the sole evaluator each tick; this
 * pure helper is shared so the contract is unambiguous and testable.
 *
 * Clear fires only when, among **currently-connected, alive** players, there is
 * at least one and **every** such player is inside the door trigger. A
 * `respawning` player is NOT counted as present-at-door (so a respawn blocks a
 * pending clear, criterion 12); a disconnected player is excluded entirely (so
 * a disconnect cannot softlock the remaining team, criterion 13).
 *
 * @param pons        the current `GameState.pons` values
 * @param connected   set of `sessionId`s currently connected (from the lobby roster)
 */
export function isStageCleared(pons, connected) {
    let eligible = 0;
    for (const pon of pons) {
        if (!connected.has(pon.sessionId))
            continue; // disconnected — excluded
        if (pon.life !== 'alive')
            return false; // a respawning player blocks clear
        eligible += 1;
        if (!pon.atDoor)
            return false; // someone is still outside the door
    }
    return eligible > 0;
}
/**
 * Whether a body centre is inside the door clear trigger. Distance is measured
 * from the door centre in tile space; `clearRadius` is the door's radius. Shared
 * so server (sim) and any client affordance agree on the trigger geometry.
 */
export function isInsideDoor(ponX, ponY, doorX, doorY, clearRadius) {
    const dx = ponX - doorX;
    const dy = ponY - doorY;
    return dx * dx + dy * dy <= clearRadius * clearRadius;
}
/**
 * 1-4-scoped "at door" test: an axis-aligned box around the door, tolerant of a
 * vertical pile of Pons. Replaces the constant-radius circle (`isInsideDoor`) for
 * stage 1-4 ONLY — at P=7/8 the scaled far perch is too narrow to ground the whole
 * party side-by-side, so Pon-on-Pon collision stacks the overflow upward; the
 * circle's tight `dy` then never admits the stacked bodies and the finale never
 * clears (P1-A). The box is tight horizontally (so a Pon must be on the perch
 * cluster, not loitering on the bridge/lip) but generous vertically up to a
 * height ceiling (so a Pon resting on the pile still counts). See
 * `docs/design/specs/1-4-finale-fix.md`. All other stages keep `isInsideDoor`.
 *
 * @param ponX        body centre x (tiles)
 * @param ponY        body centre y (tiles)
 * @param doorX       door centre x (tiles; the SCALED 1-4 door)
 * @param perchTopY   the far-perch top plane (y; a grounded centre sits at + HALF)
 * @param xBand       half-width of the box, centred on `doorX`
 * @param heightCeil  how far above `perchTopY` a Pon still counts (admits the pile)
 * @param floorTol    slack below `perchTopY` for a settling/landing body
 */
export function isInsidePerchDoor(ponX, ponY, doorX, perchTopY, xBand, heightCeil, floorTol) {
    return (Math.abs(ponX - doorX) <= xBand &&
        ponY >= perchTopY - floorTol &&
        ponY <= perchTopY + heightCeil);
}
//# sourceMappingURL=gamestate.js.map